import { z } from 'zod';

/**
 * Présentation des BORDEREAUX — page de garde civile et bordereau de productions.
 *
 * Ce module est le contrat UNIQUE des rendus :
 *   • PDF  civil — `apps/backend/services/bordereau/PdfGenerationService._renderCoverPreTable`
 *   • Word civil — `apps/legalworkspace/src/features/bordereau/utils/bordereauCoverDocxExport.ts`
 *   • Word administratif (Télérecours) — `…/utils/bordereauProductionsAdminDocxExport.ts`
 *     et sa variante récapitulative `…/utils/appendComplementaryToDocx.ts`
 *
 * Tous lisent la MÊME config résolue, à une exception assumée : la POLICE ne
 * s'applique qu'au Word (cf. `fontFamily` ci-dessous).
 *
 * **Une config, deux vocabulaires.** Ce qui relève de la charte du CABINET — police,
 * tailles, marges — est partagé par les deux bordereaux : un cabinet compose de la même
 * façon devant le tribunal judiciaire et devant le tribunal administratif. Ce que le DROIT
 * distingue — le nom du document et les intitulés de section — est dédoublé, parce que le
 * bordereau civil relève du CPC (« pièces communiquées ») et le bordereau de productions du
 * CJA (« productions », art. R. 412-2, dépôt Télérecours). Cf. `BordereauAdminLabelsSchema`.
 *
 * Il ne décrit QUE la présentation. Le contenu par dossier (juridiction, R.G.,
 * affaire, parties…) reste `CoverPageMetadata` / `AdminCoverMetadata`, saisi dans le wizard
 * bordereau.
 *
 * Hub : `docs/features/bordereau/`.
 */

/**
 * Bornes des réglages, exportées pour que les champs de saisie de l'éditeur les reprennent
 * au lieu de re-coder des `min`/`max` qui dériveraient du schéma.
 */
export const BORDEREAU_COVER_LIMITS = {
  /** Marges, en millimètres (page A4 210 × 297 mm). */
  marginMinMm: 5,
  marginMaxMm: 60,
  /** Taille du titre encadré, en points. */
  titleFontSizeMin: 8,
  titleFontSizeMax: 36,
  /** Taille du corps des sections, en points. */
  sectionFontSizeMin: 6,
  sectionFontSizeMax: 24,
  /** Longueur du nom du document. */
  documentTitleMaxLength: 120,
  /**
   * Longueur d'un libellé de section (« Pièces communiquées par : », « Page »…).
   *
   * Plus court que le nom du document : ce sont des intitulés, pas des phrases, et la
   * page de garde PDF les écrit sur UNE ligne sans retour automatique — un intitulé long
   * déborderait dans la colonne voisine du tableau des pièces. 80 caractères laissent
   * largement la place aux formules d'usage (le plus long des défauts, côté administratif,
   * en fait 40 : « LISTE DES PRODUCTIONS COMPLÉMENTAIRES DU »).
   */
  labelMaxLength: 80,
} as const;

const MARGIN_MIN_MM = BORDEREAU_COVER_LIMITS.marginMinMm;
const MARGIN_MAX_MM = BORDEREAU_COVER_LIMITS.marginMaxMm;

/**
 * Marges de la page de garde, en **millimètres**.
 *
 * Le millimètre est l'unité de l'avocat (et celle de Word) ; les deux moteurs de
 * rendu convertissent vers leur unité native (points PDF, twips Word). Plancher à
 * {@link MARGIN_MIN_MM} mm : en deçà, le contenu tombe hors de la zone imprimable
 * de la plupart des imprimantes laser, et un bordereau se dépose sur papier.
 */
export const BordereauCoverMarginsSchema = z.object({
  top: z.number().finite().min(MARGIN_MIN_MM).max(MARGIN_MAX_MM),
  bottom: z.number().finite().min(MARGIN_MIN_MM).max(MARGIN_MAX_MM),
  left: z.number().finite().min(MARGIN_MIN_MM).max(MARGIN_MAX_MM),
  right: z.number().finite().min(MARGIN_MIN_MM).max(MARGIN_MAX_MM),
});

/**
 * Un libellé de section : chaîne courte, jamais vide, **rognée** aux deux bouts.
 *
 * Le rognage n'est pas cosmétique : les moteurs de rendu recollent eux-mêmes l'espace qui
 * sépare l'intitulé de sa valeur (cf. {@link BordereauCoverLabelsSchema}). Sans lui, un
 * « Affaire :  » copié depuis Word produirait « Affaire :  SAS DURAND » avec deux espaces,
 * invisibles dans le champ de saisie et visibles sur l'acte déposé.
 */
const labelField = () =>
  z.string().trim().min(1).max(BORDEREAU_COVER_LIMITS.labelMaxLength).optional();

/**
 * Libellés des sections de la page de garde — **tous optionnels**, même sémantique
 * d'héritage que le reste : clé absente = « hérite de la couche du dessous ».
 *
 * **Convention de recollage — à lire avant d'ajouter un libellé.** Un intitulé est stocké
 * SANS son espace de séparation ; c'est le moteur de rendu qui écrit `` `${libellé} ${valeur}` ``.
 * Un libellé rogné plus un espace explicite reproduisent exactement les chaînes en dur
 * d'avant ce lot (« N° R.G. : » + « 24/01234 »), tout en gardant un champ de saisie où ce
 * qu'on lit est ce qu'on a tapé. La seule exception assumée est {@link dateSeparator},
 * collé à sa GAUCHE — cf. son TSDoc.
 *
 * Ces libellés valent pour les DEUX exports, à une réserve près : l'export Word rend les
 * pièces en LISTE (« **Pièce n°X** : label ») et non en tableau, donc les trois intitulés
 * de colonne (`piecesColumnNumber`, `piecesColumnLabel`, `piecesColumnPage`) n'ont d'effet
 * que sur le PDF. L'interface porte cette réserve à côté des champs concernés.
 */
export const BordereauCoverLabelsSchema = z.object({
  /** Précède le numéro de rôle général : « N° R.G. : 24/01234 ». */
  numeroRgPrefix: labelField(),
  /** Précède l'intitulé de l'affaire : « Affaire : SAS DURAND C/ M. MARTIN ». */
  affairePrefix: labelField(),
  /** Précède la date d'audience : « Audience du 14 janvier 2026 à 9 h 00 ». */
  audiencePrefix: labelField(),
  /** Intitulé souligné du bloc de la partie qui communique ses pièces. */
  senderHeading: labelField(),
  /**
   * Sous-intitulé « Avocat de : », utilisé dans les DEUX blocs de parties (celui qui
   * communique et le destinataire). Un seul réglage pour les deux : les distinguer
   * n'aurait aucun sens métier et doublerait la surface de saisie.
   */
  counselForHeading: labelField(),
  /** Intitulé souligné du bloc destinataire (« À : »). */
  recipientHeading: labelField(),
  /** Intitulé souligné qui introduit la liste des pièces. */
  piecesListHeading: labelField(),
  /** En-tête de la colonne des numéros — **PDF uniquement** (le Word rend une liste). */
  piecesColumnNumber: labelField(),
  /** En-tête de la colonne des désignations — **PDF uniquement**. */
  piecesColumnLabel: labelField(),
  /** En-tête de la colonne des pages — **PDF uniquement**. */
  piecesColumnPage: labelField(),
  /** Ouvre la formule de signature : « Fait à Paris, le 5 janvier 2026 ». */
  faitAPrefix: labelField(),
  /**
   * Fragment inséré entre le lieu et la date, **collé au lieu** : le rendu est
   * `` `${faitAPrefix} ${ville}${dateSeparator} ${date}` ``.
   *
   * Ce découpage en deux fragments a été préféré à un gabarit à jetons du type
   * `« Fait à {ville}, le {date} »` : un gabarit oblige l'avocat à connaître (et à ne pas
   * casser) une syntaxe de substitution, et un jeton oublié à la saisie ferait disparaître
   * la date d'un acte déposé — un silence, pas une erreur. Deux champs de texte libre ne
   * peuvent, eux, que déplacer des mots autour de valeurs toujours rendues.
   *
   * Conséquence assumée : le fragment porte sa propre ponctuation d'attaque (défaut
   * `', le'`), puisque le rognage lui interdit de porter une espace de tête.
   */
  dateSeparator: labelField(),
  /** Mention de signature, en bas à droite de la dernière page de garde. */
  signatureLabel: labelField(),
});

/** Libellés tels que l'avocat les enregistre : chaque clé absente = héritage. */
export type BordereauCoverLabels = z.infer<typeof BordereauCoverLabelsSchema>;

/** Libellés après résolution : plus aucun trou, chaque clé porte une chaîne à écrire. */
export type ResolvedBordereauCoverLabels = Readonly<Required<BordereauCoverLabels>>;

/** Clé de libellé — utile aux surfaces qui itèrent sur le catalogue (éditeur, tests). */
export type BordereauCoverLabelKey = keyof ResolvedBordereauCoverLabels;

/**
 * Catalogue fermé des libellés réglables, dans l'ordre du schéma.
 *
 * Dérivé de la forme Zod et non recopié à la main : une clé ajoutée au schéma est
 * automatiquement fusionnée par {@link resolveBordereauCoverConfig} et proposée par
 * l'éditeur, sans liste parallèle à tenir à jour.
 */
export const BORDEREAU_COVER_LABEL_KEYS = Object.keys(
  BordereauCoverLabelsSchema.shape,
) as ReadonlyArray<BordereauCoverLabelKey>;

/**
 * Libellés du **bordereau de productions** — contentieux administratif, dépôt Télérecours.
 *
 * ⚠️ Ici « admin » veut dire *administratif* (juridiction administrative), JAMAIS
 * *administrateur*. C'est le vocabulaire déjà en place dans la feature
 * (`AdminCoverMetadata`, `bordereauProductionsAdminDocxExport`).
 *
 * **Pourquoi un SECOND catalogue et non un partage avec {@link BordereauCoverLabelsSchema}.**
 * Les deux documents n'obéissent pas au même formalisme, et l'inventaire des dix-huit
 * intitulés le montre :
 *   • 4 seulement se correspondent (l'en-tête juridictionnel, la liste des pièces, le lieu
 *     et la date d'émission) ;
 *   • 3 sont des SUBSTITUTIONS sémantiques, pas des traductions : « Pièces communiquées
 *     par : » → « POUR : » (la partie *pour laquelle* on produit), « À : » (le confrère
 *     destinataire de la communication) → « CONTRE : » (la *décision attaquée*), « Avocat
 *     de : » → « Ayant pour Avocat : ». Partager la clé ferait qu'un cabinet renommant son
 *     bloc destinataire en civil réécrirait, sans le savoir, la désignation de la décision
 *     attaquée sur ses recours ;
 *   • 6 intitulés civils n'ont AUCUN objet en administratif (N° R.G., date d'audience,
 *     les trois en-têtes de colonne dont « Page », la mention de signature) ;
 *   • et l'administratif a 5 intitulés qui lui sont propres.
 * Un catalogue unique obligerait donc l'avocat à parcourir des champs marqués « sans effet
 * ici » et rendrait deux notions juridiques distinctes indissociables. Deux catalogues
 * coûtent une seconde boucle de fusion ; un catalogue partagé coûterait des actes faux.
 *
 * **Même convention de recollage que le civil** : l'intitulé est stocké avec sa ponctuation
 * (« POUR : ») mais SANS son espace de séparation — le moteur de rendu écrit
 * `` `${intitulé} ${valeur}` ``. Cf. {@link BordereauCoverLabelsSchema}.
 *
 * **Ce qui n'est volontairement PAS réglable ici**, et pourquoi :
 *   • le **format des lignes de pièces** (`« 3 Arrêté préfectoral du 12 mars 2025 »` : le
 *     numéro, une espace, le libellé, sans point) : les art. R. 412-2 et R. 414-5 / R. 611-8-5
 *     CJA (décret 2020-1245) imposent un intitulé qui commence par le numéro d'ordre, suivi
 *     d'un libellé descriptif, et INTERDISENT donc le « Pièce n°X » du CPC civil (#1704).
 *     Le rendre réglable laisserait un cabinet produire un bordereau non conforme ;
 *   • les **repères de saisie entre crochets** (`[ville]`, `[Juridiction administrative]`,
 *     `[Me X — barreau — toque — adresse]`…) : ce ne sont pas des libellés du document mais
 *     la marque visible d'un champ non rempli, adressée à celui qui rédige. Les rendre
 *     réglables permettrait d'y écrire une valeur plausible (« Paris ») qui partirait telle
 *     quelle au greffe — un trou visible deviendrait une erreur invisible. Et la bonne place
 *     d'une valeur par défaut de ville est le formulaire du dossier, pas un réglage cabinet ;
 *   • l'**astérisque séparateur** et le repli « Sans titre » : le bordereau civil porte
 *     exactement les mêmes et ne les expose pas non plus — les ouvrir d'un seul côté créerait
 *     une asymétrie que l'avocat ne pourrait pas s'expliquer.
 */
export const BordereauAdminLabelsSchema = z.object({
  /** En-tête juridictionnel, en haut à droite, au-dessus du nom de la juridiction. */
  presidentHeading: labelField(),
  /**
   * Ouvre la mention de lieu et de date d'émission : « À Lyon, le 20 août 2025 ».
   *
   * Défaut `'A'` **sans accent** — c'est la chaîne actuellement produite. Le « À » accentué
   * serait la forme correcte en français ; le corriger d'office changerait le document de
   * tous les cabinets, donc c'est à l'avocat de le faire s'il le souhaite.
   */
  emissionPlacePrefix: labelField(),
  /**
   * Fragment inséré entre le lieu et la date, **collé au lieu** — même découpage en deux
   * fragments que `dateSeparator` côté civil, et pour la même raison : un gabarit à jetons
   * ferait disparaître la date d'un acte déposé si le jeton était supprimé à la saisie.
   */
  emissionDateSeparator: labelField(),
  /** Précède le ou les numéros de dossier de la juridiction : « Dossiers : 2501878 et 2501879 ». */
  dossiersPrefix: labelField(),
  /** Intitulé du bloc de la partie pour laquelle les pièces sont produites. */
  pourHeading: labelField(),
  /**
   * Intitulé du bloc avocat.
   *
   * Le défaut dit « Avocat » et non « Avocate » : il désigne la FONCTION, pas le genre de la
   * personne. C'est aussi la formule que l'add-in Word détecte (`isAvocatBlock()`,
   * `services/insertion/wordInserter.ts`) pour appliquer son style de délimiteur — la
   * renommer dissocie le bordereau des actes générés, ce qui est un choix légitime du
   * cabinet mais doit rester un choix.
   */
  avocatHeading: labelField(),
  /** Intitulé du bloc de la décision attaquée (et non du confrère destinataire). */
  contreHeading: labelField(),
  /** Intitulé souligné qui introduit la liste des productions. */
  productionsListHeading: labelField(),
  /**
   * Ouvre l'intitulé de la section des productions complémentaires, **suivi de la date** :
   * `` `${complementaryListPrefix} 20 AOÛT 2025` ``. Utilisé par le mode récapitulatif, qui
   * greffe la section à la fin du bordereau .docx fourni par l'avocat.
   */
  complementaryListPrefix: labelField(),
});

/** Libellés administratifs tels que l'avocat les enregistre : chaque clé absente = héritage. */
export type BordereauAdminLabels = z.infer<typeof BordereauAdminLabelsSchema>;

/** Libellés administratifs après résolution : plus aucun trou. */
export type ResolvedBordereauAdminLabels = Readonly<Required<BordereauAdminLabels>>;

/** Clé de libellé administratif — utile aux surfaces qui itèrent (éditeur, tests). */
export type BordereauAdminLabelKey = keyof ResolvedBordereauAdminLabels;

/**
 * Catalogue fermé des libellés administratifs, dans l'ordre du schéma. Dérivé de la forme
 * Zod, comme son pendant civil : une clé ajoutée au schéma est automatiquement fusionnée et
 * proposée par l'éditeur, sans liste parallèle à tenir à jour.
 */
export const BORDEREAU_ADMIN_LABEL_KEYS = Object.keys(
  BordereauAdminLabelsSchema.shape,
) as ReadonlyArray<BordereauAdminLabelKey>;

/**
 * Réglages de présentation, **tous optionnels**.
 *
 * Une clé absente signifie « je n'ai rien réglé, hérite de la couche du dessous »
 * (utilisateur → cabinet → défaut). La fusion est donc CHAMP PAR CHAMP et non
 * objet par objet : un avocat qui n'a personnalisé que sa police continue de
 * recevoir le titre décidé par son cabinet. Un `??` sur l'objet entier figerait
 * au contraire tous les autres champs à la valeur qu'ils avaient le jour de son
 * enregistrement.
 */
export const BordereauCoverSettingsSchema = z.object({
  /**
   * Nom du document, affiché dans le cadre en haut de la page de garde
   * (« BORDEREAU DES PIÈCES COMMUNIQUÉES », « BORDEREAU DE COMMUNICATION DE
   * PIÈCES »…). S'applique aux DEUX exports **du bordereau civil** ; le bordereau
   * administratif a le sien, cf. {@link adminDocumentTitle}.
   */
  documentTitle: z.string().trim().min(1).max(BORDEREAU_COVER_LIMITS.documentTitleMaxLength).optional(),

  /**
   * Police de la page de garde — **export Word UNIQUEMENT**.
   *
   * En .docx la police n'est qu'un NOM, résolu par Word sur la machine du
   * lecteur : rien n'est embarqué, aucune licence n'est en jeu, les 202 familles
   * du catalogue `microsoftFonts.ts` fonctionnent.
   *
   * En PDF, la police doit être EMBARQUÉE dans le fichier. `pdf-lib` n'expose
   * nativement que les 14 polices de base PDF, et Calibri / Cambria / Bodoni MT
   * sont propriétaires Microsoft/Monotype : les embarquer dans un PDF déposé au
   * greffe demanderait une licence de redistribution que le cabinet n'a pas.
   * Obstacle juridique, pas technique — le PDF conserve donc Times Roman.
   *
   * L'interface DOIT porter cette réserve à côté du sélecteur de police : sans
   * elle l'avocat croit régler les deux et découvre l'écart sur un acte déposé.
   *
   * **Réglage PARTAGÉ entre le bordereau civil et le bordereau de productions**
   * (2026-08-15), au même titre que les tailles et les marges. La police est une
   * caractéristique du CABINET, pas de la procédure : un cabinet qui compose en Garamond
   * compose en Garamond devant le tribunal judiciaire comme devant le tribunal
   * administratif. Dédoubler le réglage obligerait à le poser deux fois, et le premier
   * bordereau administratif produit après un changement de charte sortirait dans l'ancienne
   * police sans que personne ne comprenne pourquoi. Ce qui se dédouble, c'est ce que le
   * droit distingue : le NOM du document et les INTITULÉS (cf. {@link adminDocumentTitle} et
   * {@link BordereauAdminLabelsSchema}).
   */
  fontFamily: z.string().trim().min(1).max(80).optional(),

  /**
   * Taille du titre encadré, en points. S'applique aux DEUX exports **et aux deux
   * bordereaux** (civil et administratif) — cf. la note de partage sur {@link fontFamily}.
   */
  titleFontSize: z
    .number()
    .int()
    .min(BORDEREAU_COVER_LIMITS.titleFontSizeMin)
    .max(BORDEREAU_COVER_LIMITS.titleFontSizeMax)
    .optional(),

  /**
   * Taille du texte des sections (en-tête procédural, parties, liste des
   * pièces, signature), en points. S'applique aux DEUX exports **et aux deux bordereaux**.
   */
  sectionFontSize: z
    .number()
    .int()
    .min(BORDEREAU_COVER_LIMITS.sectionFontSizeMin)
    .max(BORDEREAU_COVER_LIMITS.sectionFontSizeMax)
    .optional(),

  /**
   * Marges personnalisées, en millimètres. **Clé absente = aucune
   * personnalisation**, et chaque export conserve alors sa mise en page
   * historique — qui n'est pas la même des deux côtés :
   *   • PDF  : 80 / 60 / 50 / 50 pt (haut / bas / gauche / droite)
   *   • Word : 1 pouce sur les quatre côtés (défaut de la librairie `docx`) — vrai pour le
   *     bordereau civil comme pour le bordereau de productions, qui construisaient tous
   *     deux leur section sans `page.margin`.
   *
   * Aligner les deux d'office déplacerait la mise en page Word de tous les
   * cabinets qui n'ont rien demandé. Dès que l'avocat renseigne des marges,
   * elles s'appliquent en revanche aux DEUX exports — c'est le but du réglage.
   *
   * **Exception assumée** : le mode récapitulatif greffe sa section à la fin d'un .docx
   * FOURNI par l'avocat (`appendComplementaryToDocx`) ; il n'y touche pas aux marges, qui
   * appartiennent à son document. Réécrire le `sectPr` d'un fichier qu'on n'a pas produit
   * déplacerait tout ce qui le précède.
   */
  margins: BordereauCoverMarginsSchema.optional(),

  /**
   * Intitulés des sections de la page de garde (cf. {@link BordereauCoverLabelsSchema}).
   *
   * L'objet lui-même est optionnel ET chacune de ses clés l'est : la fusion descend d'un
   * niveau, contrairement à `margins` qui se remplace en bloc. Un avocat qui ne renomme
   * que la colonne « Désignation de la pièce » garde donc les douze autres intitulés
   * décidés par son cabinet. Les marges, elles, forment un tout géométrique — en hériter
   * une seule ferait mentir l'aperçu sur la mise en page réelle.
   */
  labels: BordereauCoverLabelsSchema.optional(),

  /**
   * Nom du document du **bordereau de productions** (administratif, Télérecours), affiché
   * dans le cadre centré : « BORDEREAU DE PRODUCTIONS », « BORDEREAU DE PRODUCTIONS
   * COMPLÉMENTAIRES »…
   *
   * Réglage DISTINCT de {@link documentTitle}, et c'est le point le moins négociable du lot :
   * un cabinet qui renomme sa page de garde civile en « BORDEREAU DE COMMUNICATION DE
   * PIÈCES » ne veut évidemment pas voir ce titre en tête d'un recours déposé sur
   * Télérecours — « pièces communiquées » est le vocabulaire du CPC, « productions » celui
   * du CJA. Une clé partagée aurait rendu ce mélange non seulement possible mais probable.
   */
  adminDocumentTitle: z
    .string()
    .trim()
    .min(1)
    .max(BORDEREAU_COVER_LIMITS.documentTitleMaxLength)
    .optional(),

  /**
   * Intitulés du bordereau de productions (cf. {@link BordereauAdminLabelsSchema}).
   *
   * Même sémantique d'héritage que {@link labels} : l'objet est optionnel ET chacune de ses
   * clés l'est, la fusion descend d'un niveau. Un avocat qui ne renomme que « CONTRE : »
   * garde les huit autres intitulés décidés par son cabinet.
   */
  adminLabels: BordereauAdminLabelsSchema.optional(),

  /**
   * Reprendre le LOGO de l'en-tête du cabinet en haut du bordereau
   * (`cabinet_settings.letterhead_settings.image`, déposé dans Personnalisation →
   * Charte graphique → « En-tête du cabinet »).
   *
   * **Portée : les DEUX bordereaux, et les trois moteurs de rendu** — page de garde civile
   * (PDF et Word) et bordereau de productions administratif (Word), où le logo ouvre la
   * première page AU-DESSUS de l'adresse à la juridiction.
   *
   * **Opt-in strict, défaut `false`** : un cabinet qui n'a rien demandé ne doit voir
   * aucune différence sur ses bordereaux — un logo qui apparaîtrait tout seul sur un acte
   * déposé au greffe serait une régression, pas une amélioration.
   *
   * Seule l'IMAGE est reprise, jamais les champs texte de l'en-tête (nom du cabinet,
   * adresse, barreau, téléphone…) : les deux bordereaux portent DÉJÀ l'identité du cabinet
   * dans leur bloc avocat (« Pièces communiquées par : » en civil, « Ayant pour Avocat : »
   * en administratif), et les réimprimer en haut de page ferait doublon sur le même document.
   *
   * **Une seule case pour les deux documents — décision produit du 2026-08-15.** Le lot du
   * 2026-08-14 avait réservé ce réglage au bordereau civil, au motif que le haut de la
   * première page administrative est occupé par l'adresse à la juridiction. L'arbitrage a été
   * revu : un cabinet qui demande « affiche mon en-tête » l'a demandé pour son papier, pas
   * pour une procédure — ne pas le voir sur ses productions serait la surprise, et deux cases
   * pour un seul geste seraient une question de plus posée à l'avocat sans contrepartie.
   * **Conséquence assumée et connue** : les cabinets déjà opt-in voient leur bordereau de
   * productions changer au prochain export. C'est voulu ; ce n'est pas un débordement
   * accidentel de la portée civile, et personne ne doit le « corriger » en re-dédoublant le
   * réglage. Ce qui se dédouble reste ce que le DROIT distingue — le nom du document et les
   * intitulés (cf. {@link adminDocumentTitle} et {@link BordereauAdminLabelsSchema}).
   */
  showLetterhead: z.boolean().optional(),
});

/**
 * Hauteur maximale du logo d'en-tête en tête de bordereau, en millimètres.
 *
 * Borne partagée par les deux systèmes d'unités de rendu (points PDF d'un côté, pixels
 * `docx` de l'autre) : sans elle, un logo déposé en 2000 px de haut mangerait la première
 * page entière et repousserait la liste des pièces sur une deuxième page. 40 mm ≈ 1/7ᵉ de la
 * hauteur A4, l'ordre de grandeur d'un en-tête de papier à lettres de cabinet.
 */
export const BORDEREAU_COVER_LETTERHEAD_MAX_HEIGHT_MM = 40;

/**
 * Résolution supposée d'une image d'en-tête sans dimension explicite : **96 ppp**.
 *
 * Le choix doit être EXPLICITE parce que les deux librairies de rendu ne s'accordent pas
 * toutes seules : `pdf-lib` traite `image.width` comme des points (72 ppp), la librairie
 * `docx` traite `transformation.width` comme des pixels d'écran (96 ppp). Une même image
 * sans `widthMm` sortirait donc 33 % plus grande en PDF qu'en Word. En passant par une
 * taille en MILLIMÈTRES calculée ici une seule fois, les deux exports affichent le même
 * logo à la même taille.
 */
const LETTERHEAD_IMAGE_PX_PER_MM = 96 / 25.4;

/** Géométrie du logo d'en-tête, en millimètres — unité commune aux deux exports. */
export interface BordereauCoverLetterheadSize {
  readonly widthMm: number;
  readonly heightMm: number;
}

/**
 * Calcule la taille d'affichage du logo d'en-tête en tête de bordereau — page de garde
 * civile comme bordereau de productions.
 *
 * Fonction PURE et partagée pour la même raison que {@link resolveBordereauCoverConfig} :
 * le PDF est rendu par un worker Node et le Word par le navigateur ; deux calculs séparés
 * dériveraient sans que rien ne le signale, et l'écart ne se verrait que sur deux fichiers
 * ouverts côte à côte.
 *
 * Le rapport hauteur/largeur est TOUJOURS préservé : un logo de cabinet déformé sur un acte
 * déposé est immédiatement visible.
 *
 * @param params.naturalWidthPx Largeur native de l'image, en pixels.
 * @param params.naturalHeightPx Hauteur native de l'image, en pixels.
 * @param params.requestedWidthMm Largeur voulue par l'avocat (`letterhead_settings.image.widthMm`),
 *   réglée dans l'écran « En-tête du cabinet ». Absente ⇒ taille native à 96 ppp.
 * @param params.availableWidthMm Largeur utile de la page (A4 moins les marges).
 * @returns La taille à dessiner, ou `null` si les dimensions natives sont inexploitables
 *   (image illisible) — l'appelant produit alors le bordereau sans en-tête.
 */
export function fitBordereauCoverLetterhead(params: {
  naturalWidthPx: number;
  naturalHeightPx: number;
  requestedWidthMm?: number | null;
  availableWidthMm: number;
}): BordereauCoverLetterheadSize | null {
  const { naturalWidthPx, naturalHeightPx, requestedWidthMm, availableWidthMm } = params;
  if (
    !Number.isFinite(naturalWidthPx) ||
    !Number.isFinite(naturalHeightPx) ||
    naturalWidthPx <= 0 ||
    naturalHeightPx <= 0 ||
    !Number.isFinite(availableWidthMm) ||
    availableWidthMm <= 0
  ) {
    return null;
  }

  const nativeWidthMm = naturalWidthPx / LETTERHEAD_IMAGE_PX_PER_MM;
  const nativeHeightMm = naturalHeightPx / LETTERHEAD_IMAGE_PX_PER_MM;
  // Une largeur explicite peut AGRANDIR l'image au-delà de sa taille native : c'est un
  // réglage de mise en page assumé par l'avocat (même sémantique que dans l'add-in Word),
  // et le seul plafond qui compte reste la page.
  const desiredWidthMm =
    requestedWidthMm !== null && requestedWidthMm !== undefined && requestedWidthMm > 0
      ? requestedWidthMm
      : nativeWidthMm;

  const scale = Math.min(
    desiredWidthMm / nativeWidthMm,
    availableWidthMm / nativeWidthMm,
    BORDEREAU_COVER_LETTERHEAD_MAX_HEIGHT_MM / nativeHeightMm,
  );

  return { widthMm: nativeWidthMm * scale, heightMm: nativeHeightMm * scale };
}

/**
 * Config effectivement consommée par les moteurs de rendu : chaque champ vaut
 * l'override le plus spécifique, ou le défaut historique.
 *
 * `margins: null` signifie « aucune marge personnalisée » — chaque export garde
 * sa mise en page d'origine (cf. {@link BordereauCoverSettingsSchema.shape.margins}).
 */
export interface ResolvedBordereauCoverConfig {
  readonly documentTitle: string;
  readonly fontFamily: string;
  readonly titleFontSize: number;
  readonly sectionFontSize: number;
  readonly margins: BordereauCoverMargins | null;
  readonly labels: ResolvedBordereauCoverLabels;
  /** Nom du document du bordereau de productions (administratif). */
  readonly adminDocumentTitle: string;
  /** Intitulés du bordereau de productions (administratif). */
  readonly adminLabels: ResolvedBordereauAdminLabels;
  readonly showLetterhead: boolean;
}

/**
 * Valeurs par défaut — elles reproduisent le rendu ACTUEL des deux exports à
 * l'identique. Un cabinet qui ne touche à rien ne doit voir aucune différence.
 *
 * Correspondances vérifiées le 2026-08-11 :
 *   • `documentTitle` — PDF `config.pdf.indexPage.title` et Word (chaîne en dur)
 *     portaient déjà tous deux « BORDEREAU DES PIÈCES COMMUNIQUÉES ».
 *   • `titleFontSize` 14 — PDF `TITLE_SIZE = 14`, Word `TITLE_SIZE = 28`
 *     demi-points = 14 pt.
 *   • `sectionFontSize` 11 — PDF `SIZE = 11`, Word `SIZE = 22` demi-points = 11 pt.
 *   • `fontFamily` — Word `FONT = 'Times New Roman'` ; le PDF embarque
 *     `StandardFonts.TimesRoman`, la même famille.
 *   • `labels` — les treize chaînes qui étaient en dur dans les deux moteurs de rendu,
 *     recopiées **au caractère près** (espaces de séparation retirés, cf. la convention
 *     de recollage de {@link BordereauCoverLabelsSchema}). C'est la garantie de
 *     non-régression du lot « libellés » : `cover-settings.test.ts` fige chacune d'elles.
 *   • `adminDocumentTitle` / `adminLabels` (lot « bordereau de productions », 2026-08-15) —
 *     les dix chaînes qui étaient en dur dans `bordereauProductionsAdminDocxExport.ts` et
 *     `appendComplementaryToDocx.ts`, recopiées elles aussi au caractère près, y compris
 *     leurs particularités : « À MADAME » accentué, et la ponctuation `' : '` que
 *     `labeledBlock` recollait autour du deux-points (l'intitulé porte désormais son
 *     deux-points, le moteur ne recolle plus qu'une espace). UNE seule chaîne s'écarte
 *     de l'historique — `emissionPlacePrefix`, passé de « A » à « À » : cf. son défaut.
 *   • `showLetterhead: false` — aucun bordereau ne portait de logo avant le lot
 *     « en-tête » (2026-08-14). Le passer à `true` ferait apparaître le logo du cabinet
 *     sur les bordereaux de TOUS les cabinets qui en ont déposé un, sans l'avoir demandé.
 *
 * ⚠️ `config/bordereau.config.ts` annonçait historiquement
 * `fontSize: { title: 20, header: 11, body: 10 }`. Ces trois nombres n'étaient
 * lus par AUCUN code : seul `title` (la chaîne) l'était. Les reprendre comme
 * défauts aurait fait passer le titre de 14 à 20 pt sur tous les bordereaux —
 * une régression déguisée en refactor. Les défauts ci-dessous sont mesurés sur
 * le rendu réel, pas sur cette configuration morte.
 */
export const BORDEREAU_COVER_DEFAULTS: ResolvedBordereauCoverConfig = {
  documentTitle: 'BORDEREAU DES PIÈCES COMMUNIQUÉES',
  fontFamily: 'Times New Roman',
  titleFontSize: 14,
  sectionFontSize: 11,
  margins: null,
  labels: {
    numeroRgPrefix: 'N° R.G. :',
    affairePrefix: 'Affaire :',
    audiencePrefix: 'Audience du',
    senderHeading: 'Pièces communiquées par :',
    counselForHeading: 'Avocat de :',
    recipientHeading: 'À :',
    piecesListHeading: 'Liste des pièces communiquées :',
    // « N° » s'écrit avec le SIGNE DEGRÉ U+00B0, comme dans les deux moteurs de rendu
    // (le PDF l'écrivait littéralement `'N°'`). L'indicateur ordinal masculin
    // U+00BA lui ressemble mais ne s'encode pas pareil — un test fige le point de code.
    piecesColumnNumber: 'N°',
    piecesColumnLabel: 'Désignation de la pièce',
    piecesColumnPage: 'Page',
    faitAPrefix: 'Fait à',
    dateSeparator: ', le',
    signatureLabel: "SIGNATURE DE L'AVOCAT",
  },
  adminDocumentTitle: 'BORDEREAU DE PRODUCTIONS',
  adminLabels: {
    presidentHeading: 'À MADAME, MONSIEUR LE PRÉSIDENT',
    // Seul défaut de ce lot qui NE reproduit PAS la chaîne historique. L'export écrivait
    // « A Paris, le … » sans accent — une faute de typographie française sur un acte
    // déposé au greffe. Arbitrage produit du 2026-08-15 : on corrige pour tout le monde
    // plutôt que d'attendre que chaque cabinet répare lui-même. Le document de tout
    // cabinet n'ayant rien réglé change donc à ce seul caractère ; qui tenait au « A »
    // nu peut le reposer via le réglage.
    emissionPlacePrefix: 'À',
    emissionDateSeparator: ', le',
    dossiersPrefix: 'Dossiers :',
    // Les trois intitulés de bloc portent leur deux-points, comme leurs équivalents civils
    // (« Affaire : ») : le moteur de rendu ne recolle qu'une espace. Avant ce lot, la
    // constante valait `'POUR'` et le rendu écrivait `` `${label} : ` `` — même sortie,
    // mais un champ de saisie où l'avocat n'aurait pas vu la ponctuation qu'il édite.
    pourHeading: 'POUR :',
    avocatHeading: 'Ayant pour Avocat :',
    contreHeading: 'CONTRE :',
    productionsListHeading: 'LISTE DES PRODUCTIONS',
    complementaryListPrefix: 'LISTE DES PRODUCTIONS COMPLÉMENTAIRES DU',
  },
  showLetterhead: false,
};

/**
 * Corps d'un PATCH de réglages (cabinet ou utilisateur).
 *
 * `settings: null` EFFACE la couche visée — l'avocat repasse sur le réglage du
 * cabinet, ou le cabinet sur le défaut Hector. C'est pour cette raison que le
 * corps enveloppe l'objet : un `{}` nu voudrait dire « tous les champs remis à
 * l'héritage », ce qui est ambigu avec « efface ma ligne ».
 */
export const BordereauCoverPatchSchema = z.object({
  settings: BordereauCoverSettingsSchema.nullable(),
});

/**
 * Réponse du GET : les deux couches brutes (pour que l'éditeur sache ce qui est
 * hérité et ce qui est personnel) ET la config résolue (pour l'aperçu et les
 * exports, qui ne doivent pas refaire la fusion eux-mêmes).
 */
export const BordereauCoverResponseSchema = z.object({
  cabinet: BordereauCoverSettingsSchema.nullable(),
  user: BordereauCoverSettingsSchema.nullable(),
  resolved: z.object({
    documentTitle: z.string(),
    fontFamily: z.string(),
    titleFontSize: z.number(),
    sectionFontSize: z.number(),
    margins: BordereauCoverMarginsSchema.nullable(),
    // `.required()` et non une seconde liste de clés : la config résolue est le MÊME
    // catalogue de libellés, sans trou. Une clé ajoutée au schéma d'entrée devient donc
    // automatiquement obligatoire ici, sans risque d'oubli.
    labels: BordereauCoverLabelsSchema.required(),
    adminDocumentTitle: z.string(),
    adminLabels: BordereauAdminLabelsSchema.required(),
    showLetterhead: z.boolean(),
  }),
});

export type BordereauCoverMargins = z.infer<typeof BordereauCoverMarginsSchema>;
export type BordereauCoverSettings = z.infer<typeof BordereauCoverSettingsSchema>;
export type BordereauCoverPatch = z.infer<typeof BordereauCoverPatchSchema>;
export type BordereauCoverResponse = z.infer<typeof BordereauCoverResponseSchema>;

/** Couches de réglages, de la moins spécifique à la plus spécifique. */
export interface BordereauCoverLayers {
  /** Défaut du cabinet — `cabinet_settings.bordereau_cover_settings`. */
  readonly cabinet?: BordereauCoverSettings | null;
  /** Override personnel — `user_bordereau_cover_preferences.settings`. */
  readonly user?: BordereauCoverSettings | null;
}

/**
 * Fusionne les libellés **clé par clé**, avec la même précédence que le reste.
 *
 * La descente d'un niveau est le point subtil : `user.labels` n'écrase pas
 * `cabinet.labels` en bloc. Un avocat qui a renommé une seule colonne persiste
 * `{ labels: { piecesColumnLabel } }` ; sans cette boucle, les douze autres intitulés
 * décidés par son cabinet retomberaient sur les défauts Hector — une régression
 * silencieuse, visible seulement sur un acte déjà déposé.
 */
function resolveBordereauCoverLabels(
  user: BordereauCoverLabels | undefined,
  cabinet: BordereauCoverLabels | undefined,
): ResolvedBordereauCoverLabels {
  const resolved = { ...BORDEREAU_COVER_DEFAULTS.labels };
  for (const key of BORDEREAU_COVER_LABEL_KEYS) {
    resolved[key] = user?.[key] ?? cabinet?.[key] ?? BORDEREAU_COVER_DEFAULTS.labels[key];
  }
  return resolved;
}

/**
 * Fusionne les libellés du bordereau de productions, **clé par clé**.
 *
 * Jumeau exact de {@link resolveBordereauCoverLabels} sur le second catalogue : deux boucles
 * séparées, parce que les deux catalogues sont séparés (cf. {@link BordereauAdminLabelsSchema}
 * pour le pourquoi). Les factoriser derrière une fonction générique gagnerait trois lignes et
 * coûterait la lisibilité du seul endroit où la précédence est écrite.
 */
function resolveBordereauAdminLabels(
  user: BordereauAdminLabels | undefined,
  cabinet: BordereauAdminLabels | undefined,
): ResolvedBordereauAdminLabels {
  const resolved = { ...BORDEREAU_COVER_DEFAULTS.adminLabels };
  for (const key of BORDEREAU_ADMIN_LABEL_KEYS) {
    resolved[key] = user?.[key] ?? cabinet?.[key] ?? BORDEREAU_COVER_DEFAULTS.adminLabels[key];
  }
  return resolved;
}

/**
 * Applique la précédence **utilisateur → cabinet → défaut**, champ par champ.
 *
 * Fonction PURE : aucun accès réseau ni base. Les appelants (route API, worker
 * PDF, éditeur front) lui passent les deux lignes déjà chargées. C'est ce qui
 * permet de figer la règle de précédence dans un test unitaire, alors que les
 * deux moteurs de rendu, eux, ne sont testables qu'en produisant un fichier.
 *
 * @param layers Les deux couches de réglages ; `null`/`undefined` = couche absente.
 * @returns La config complète à passer aux moteurs de rendu.
 *
 * @example
 * // L'avocat n'a personnalisé que sa police : il garde le titre du cabinet.
 * resolveBordereauCoverConfig({
 *   cabinet: { documentTitle: 'BORDEREAU DE COMMUNICATION DE PIÈCES' },
 *   user: { fontFamily: 'Garamond' },
 * });
 * // → { documentTitle: 'BORDEREAU DE COMMUNICATION DE PIÈCES', fontFamily: 'Garamond',
 * //     titleFontSize: 14, sectionFontSize: 11, margins: null,
 * //     labels: BORDEREAU_COVER_DEFAULTS.labels, showLetterhead: false }
 */
export function resolveBordereauCoverConfig(
  layers: BordereauCoverLayers = {},
): ResolvedBordereauCoverConfig {
  const cabinet = layers.cabinet ?? undefined;
  const user = layers.user ?? undefined;

  // `??` et non `||` : une chaîne vide ou un 0 sont déjà exclus par le schéma Zod,
  // mais `??` garde la sémantique juste si les bornes évoluent — seul `undefined`
  // (clé absente) déclenche l'héritage.
  return {
    documentTitle:
      user?.documentTitle ?? cabinet?.documentTitle ?? BORDEREAU_COVER_DEFAULTS.documentTitle,
    fontFamily: user?.fontFamily ?? cabinet?.fontFamily ?? BORDEREAU_COVER_DEFAULTS.fontFamily,
    titleFontSize:
      user?.titleFontSize ?? cabinet?.titleFontSize ?? BORDEREAU_COVER_DEFAULTS.titleFontSize,
    sectionFontSize:
      user?.sectionFontSize ?? cabinet?.sectionFontSize ?? BORDEREAU_COVER_DEFAULTS.sectionFontSize,
    margins: user?.margins ?? cabinet?.margins ?? BORDEREAU_COVER_DEFAULTS.margins,
    labels: resolveBordereauCoverLabels(user?.labels, cabinet?.labels),
    adminDocumentTitle:
      user?.adminDocumentTitle ??
      cabinet?.adminDocumentTitle ??
      BORDEREAU_COVER_DEFAULTS.adminDocumentTitle,
    adminLabels: resolveBordereauAdminLabels(user?.adminLabels, cabinet?.adminLabels),
    // `??` est ici indispensable, `||` serait FAUX : un avocat qui coupe l'en-tête que son
    // cabinet a activé persiste `showLetterhead: false`, une valeur fausse mais bien
    // présente. Avec `||`, elle retomberait sur le `true` du cabinet — l'avocat verrait
    // son réglage ignoré sans aucun message.
    showLetterhead:
      user?.showLetterhead ?? cabinet?.showLetterhead ?? BORDEREAU_COVER_DEFAULTS.showLetterhead,
  };
}

/**
 * Parse une valeur JSONB venue de la base (colonne `bordereau_cover_settings` ou
 * `user_bordereau_cover_preferences.settings`).
 *
 * Une ligne illisible ne doit JAMAIS faire échouer une génération de bordereau :
 * l'avocat perdrait son acte à cause d'un réglage cosmétique. On retombe donc sur
 * `null` (= couche absente) et on laisse l'appelant journaliser.
 *
 * @param raw Valeur brute lue en base (`unknown` — le driver `pg` ne type rien).
 * @returns Les réglages validés, ou `null` si la ligne est absente ou corrompue.
 */
export function parseBordereauCoverSettings(raw: unknown): BordereauCoverSettings | null {
  if (raw === null || raw === undefined) return null;
  const parsed = BordereauCoverSettingsSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
